import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { FileText, Download, Search } from 'lucide-react';

interface Invoice {
  id: string;
  orderId: string;
  amount: number;
  baseAmount?: number;
  vatAmount?: number;
  createdAt: string;
  description: string;
  invoicePdf: string;
  type: 'commission' | 'order_payment';
  invoiceStatus: string;
}

export default function Invoices() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const { currentUser } = useAuth();

  useEffect(() => {
    if (currentUser?.companyId) {
      loadInvoices();
    }
  }, [currentUser]);

  const loadInvoices = async () => {
    try {
      const transactionsRef = collection(db, 'walletTransactions');
      
      // Query for user's commission invoices
      const commissionsQuery = query(
        transactionsRef,
        where('type', '==', 'commission'),
        where('vendorId', '==', currentUser?.companyId)
      );

      // Query for company's regular invoices
      const companyQuery = query(
        transactionsRef,
        where('companyId', '==', currentUser?.companyId),
        where('invoiced', '==', true)
      );

      const [commissionsSnapshot, companySnapshot] = await Promise.all([
        getDocs(commissionsQuery),
        getDocs(companyQuery)
      ]);

      const allInvoices = [
        ...commissionsSnapshot.docs
          .filter(doc => doc.data().invoicePdf) // Filter after query to ensure we get docs with invoicePdf
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              orderId: data.orderId,
              description: `Comissão da Encomenda ${data.orderId}`,
              amount: data.amount,
              baseAmount: data.baseAmount,
              vatAmount: data.vatAmount,
              createdAt: data.createdAt,
              invoicePdf: data.invoicePdf,
              type: 'commission' as const
            };
          }),
        ...companySnapshot.docs
          .filter(doc => doc.data().invoicePdf)
          .map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              orderId: data.orderId,
              description: data.description || `Pagamento da Encomenda ${data.orderId}`,
              amount: data.amount,
              baseAmount: data.baseAmount,
              vatAmount: data.vatAmount,
              createdAt: data.createdAt,
              invoicePdf: data.invoicePdf,
              type: 'order_payment' as const
            };
          })
      ];

      // Sort by date descending
      allInvoices.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setInvoices(allInvoices);
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError('Erro ao carregar faturas');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (invoice: Invoice) => {
    try {
      const link = document.createElement('a');
      link.href = invoice.invoicePdf;
      link.download = `fatura-${invoice.orderId}-${invoice.type}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading invoice:', err);
      setError('Erro ao baixar fatura');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatAmount = (amount: number, baseAmount?: number, vatAmount?: number) => {
    if (baseAmount && vatAmount) {
      return (
        <div>
          <p>Base: €{Math.abs(baseAmount).toFixed(2)}</p>
          <p>IVA: €{Math.abs(vatAmount).toFixed(2)}</p>
          <p className="font-bold">Total: €{Math.abs(amount).toFixed(2)}</p>
        </div>
      );
    }
    return `€${Math.abs(amount).toFixed(2)}`;
  };

  const filteredInvoices = invoices.filter(invoice =>
    invoice.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-secondary flex items-center gap-2">
              <FileText className="text-primary" />
              Minhas Faturas
            </h2>
          </div>

          <div className="mb-6 relative">
            <input
              type="text"
              placeholder="Pesquisar por número de encomenda ou descrição..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          </div>

          {error && (
            <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
              {error}
            </div>
          )}

          {filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma fatura encontrada</h3>
              <p className="text-gray-500">
                Não existem faturas disponíveis para download neste momento.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nº Encomenda
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Descrição
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {formatDate(invoice.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {invoice.orderId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          invoice.type === 'commission' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {invoice.type === 'commission' ? 'Comissão' : 'Pagamento'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {invoice.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {formatAmount(invoice.amount, invoice.baseAmount, invoice.vatAmount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleDownload(invoice)}
                          className="text-primary hover:text-primary-dark flex items-center gap-2"
                          title="Download Fatura"
                        >
                          <Download size={20} />
                          <span>Download</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}